/**
 * Dashboard — pure view-model helpers.
 *
 * Everything in here is side-effect free so the tricky parts of the page (the
 * notify gate, which is a CROSS-PAYLOAD join, and the client-side filter) can be
 * reasoned about — and later tested — without React or the network.
 *
 * The KPI counters intentionally read `DashboardSummary` instead of
 * recomputing anything client-side: `_summarise()` in
 * app/services/dashboard_service.py runs AFTER the `only_affected` filter, so
 * every tile describes the payload the server just sent, not the whole CMDB.
 */

import { t } from '@lingui/core/macro';

import { toneForCount } from '../../lib/format';
import { intlLocale } from '../../lib/locale';
import type {
  BenachrichtigungBlock,
  DashboardSummary,
  IsoDateTime,
  KpiCounter,
  OverviewRow,
  VulnerabilityEventRow,
} from '../../lib/types';

/** Auto-refresh period, matching the 60 s of the vanilla dashboard. */
export const AUTO_REFRESH_MS = 60_000;

/** Trimmed value or the given (translated) fallback — never an empty cell. */
export function orFallback(value: string | null | undefined, fallback: string): string {
  const text = (value ?? '').trim();
  return text === '' ? fallback : text;
}

/* ------------------------------------------------------------------------- *
 * KPI tiles
 * ------------------------------------------------------------------------- */

/** Reporting deadlines due now (DEV-133): overdue or running out within 24 h. */
export interface DueDeadlines {
  due: number;
  overdue: number;
}

/** Stable identity of a KPI tile; the label is translated and never a lookup key. */
export type DashboardTileKey = 'deadlines' | 'assessments' | 'notify' | 'kev' | 'customers';

export interface DashboardCounter extends KpiCounter {
  key: DashboardTileKey;
}

/**
 * The counters of the KPI strip, the most urgent first (DEV-133): due
 * reporting deadlines (only with triage.read), open assessments, customer
 * mails to send, KEV CVEs, affected customers.
 *
 * @param offeneBewertungen - `VulnerabilityEventsResponse.offen`; does not
 *   come from the overview payload.
 * @param toNotify - open customer mails: per customer × case from the CVE
 *   payload, otherwise the instances in the „Zu benachrichtigen“ section.
 * @param deadlines - null without triage.read: the tile is left out.
 */
export function kpiCounters(
  summary: DashboardSummary,
  offeneBewertungen: number,
  toNotify: number,
  deadlines: DueDeadlines | null,
): DashboardCounter[] {
  const counters: DashboardCounter[] = [];
  if (deadlines !== null) {
    counters.push({
      key: 'deadlines',
      label: t`Fällige Meldefristen`,
      value: deadlines.due,
      tone: deadlines.overdue > 0 ? 'critical' : toneForCount(deadlines.due, 'warn'),
      title: t`Offene Fristen der Behördenmeldung, die überschritten sind oder innerhalb von 24 Stunden ablaufen. Gemeldete Fristen zählen nicht.`,
    });
  }
  counters.push(
    {
      key: 'assessments',
      label: t`Offene Bewertungen`,
      value: offeneBewertungen,
      tone: toneForCount(offeneBewertungen, 'warn'),
      title: t`KEV-Treffer, an denen noch etwas zu bewerten ist: Komponente, Produktversionen oder eine erneut zu prüfende Bewertung. Erst eine bestätigte Produktbewertung „Betroffen“ gibt die Kundenbenachrichtigung frei.`,
    },
    {
      key: 'notify',
      label: t`Zu benachrichtigen`,
      value: toNotify,
      tone: toneForCount(toNotify, 'warn'),
      title: t`Kundenmails je Kunde und Schwachstellenfall, die noch nicht eingereiht oder versendet sind.`,
    },
    {
      key: 'kev',
      label: t`KEV-CVEs`,
      value: summary.kev_cves,
      tone: summary.kev_cves > 0 ? 'critical' : 'good',
      title: t`Verschiedene aktiv ausgenutzte CVEs aus den in Dependency-Track aktivierten KEV-Quellen.`,
    },
    {
      key: 'customers',
      label: t`Betroffene Kunden`,
      value: summary.customers_affected,
      tone: toneForCount(summary.customers_affected),
      title: t`Kunden mit mindestens einem betroffenen Produkt.`,
    },
  );
  return counters;
}

/* ------------------------------------------------------------------------- *
 * Client-side filtering
 * ------------------------------------------------------------------------- */

/**
 * Split the free-text box into lower-cased tokens. All tokens must match (AND),
 * which makes `siemens cve-2024` a useful query instead of a dead end.
 */
export function searchTokens(input: string): string[] {
  return input
    .toLowerCase()
    .split(/\s+/)
    .map((token) => token.trim())
    .filter((token) => token !== '');
}

/** Everything the free-text filter searches in, lower-cased and joined. */
export function rowHaystack(row: OverviewRow): string {
  const parts: (string | null)[] = [
    row.kunde.name,
    row.kunde.email,
    row.produkttyp.bezeichnung,
    row.produkttyp.modellreihe,
    row.produkt.seriennummer,
    row.produkt.bezeichnung,
    row.produktversion.version_nr,
    row.produktversion.name,
    ...row.kev_cves,
  ];
  for (const komponente of row.komponenten) {
    parts.push(komponente.name, komponente.version, komponente.hersteller, ...komponente.kev_cves);
  }
  return parts
    .filter((part): part is string => typeof part === 'string' && part !== '')
    .join(' ')
    .toLowerCase();
}

/** Client-side free-text filter; the work sections split the rows afterwards. */
export function matchesFilters(row: OverviewRow, tokens: readonly string[]): boolean {
  if (tokens.length === 0) return true;
  const haystack = rowHaystack(row);
  return tokens.every((token) => haystack.includes(token));
}

/* ------------------------------------------------------------------------- *
 * The notify gate — a CROSS-PAYLOAD JOIN
 * ------------------------------------------------------------------------- */

/**
 * What the "Benachrichtigung" column may offer for one row.
 *
 * `ready` is reachable ONLY through the vulnerability-events payload: an
 * `OverviewRow` knows that a component is KEV-affected, but not whether a human
 * decided the CVE is actually applicable. Both payloads must therefore be loaded
 * together — with an empty event list every affected row degrades to
 * `awaiting-bewertung`, which is exactly what the legacy page did.
 */
export type NotifyGate =
  /** No KEV hit at all — nothing to report. */
  | { readonly kind: 'clean' }
  /** At least one Benachrichtigung exists and none is still OFFEN. */
  | { readonly kind: 'notified'; readonly versendetAm: IsoDateTime | null; readonly openCount: number }
  /** KEV hit, but no confirmed product verdict AFFECTED has created a notification (yet). */
  | { readonly kind: 'awaiting-bewertung'; readonly openEvents: number; readonly totalEvents: number }
  /**
   * A confirmed product AFFECTED created notifications that still have to be sent.
   * `queuedCount` of them wait in the Postausgang, `failedCount` failed there (DEV-93).
   */
  | {
      readonly kind: 'ready';
      readonly openCount: number;
      readonly queuedCount: number;
      readonly failedCount: number;
      readonly kundeResolvable: boolean;
    };

/** Events whose fan-out touches this ProduktInstanz. */
export function eventsForRow(
  row: OverviewRow,
  events: readonly VulnerabilityEventRow[],
): VulnerabilityEventRow[] {
  return events.filter((event) =>
    event.betroffene.some((betroffen) => betroffen.instanz_id === row.produkt.id),
  );
}

/** Newest `versendet_am` of a row's Benachrichtigungen, or null. */
export function lastVersendetAm(
  benachrichtigungen: readonly BenachrichtigungBlock[],
): IsoDateTime | null {
  let latest: IsoDateTime | null = null;
  for (const block of benachrichtigungen) {
    const value = block.versendet_am;
    if (value === null || value === '') continue;
    // ISO-8601 from `_iso_or_none` — lexical comparison is chronological.
    if (latest === null || value > latest) latest = value;
  }
  return latest;
}

const SENT_STATES: ReadonlySet<string> = new Set(['VERSENDET', 'GEMELDET', 'ABGESCHLOSSEN']);

/** Benachrichtigung card ids of this row that did not go out yet — queued ones included (DEV-93). */
export function openBenachrichtigungIds(row: OverviewRow): number[] {
  return row.benachrichtigungen
    .filter((block) => !SENT_STATES.has(block.status) && block.id !== null)
    .map((block) => block.id as number);
}

function countStatus(row: OverviewRow, ...states: string[]): number {
  return row.benachrichtigungen.filter((block) => states.includes(block.status)).length;
}

/** Decide what the notify column may offer. See {@link NotifyGate}. */
export function notifyGate(row: OverviewRow, events: readonly VulnerabilityEventRow[]): NotifyGate {
  if (row.affected_count === 0) return { kind: 'clean' };
  if (row.notified) {
    return {
      kind: 'notified',
      versendetAm: lastVersendetAm(row.benachrichtigungen),
      openCount: openBenachrichtigungIds(row).length,
    };
  }
  // Notifications exist only after a confirmed product verdict AFFECTED (DEV-100).
  const openCount = openBenachrichtigungIds(row).length;
  if (openCount > 0) {
    return {
      kind: 'ready',
      openCount,
      queuedCount: countStatus(row, 'QUEUED', 'SENDING'),
      failedCount: countStatus(row, 'FAILED'),
      kundeResolvable: row.kunde.id !== null,
    };
  }
  const own = eventsForRow(row, events);
  return {
    kind: 'awaiting-bewertung',
    // Open = a product version of this hit still lacks a confirmed verdict.
    openEvents: own.filter((event) => {
      const products = event.assessment?.products;
      return products === undefined ? event.bewertung === 'OFFEN' : products.confirmed < products.total;
    }).length,
    totalEvents: own.length,
  };
}

/* ------------------------------------------------------------------------- *
 * Links
 * ------------------------------------------------------------------------- */

/**
 * Build the `mailto:` for a drafted advisory. The notify payload (unlike the
 * triage e-mail drafts) carries no ready-made link, so it is assembled here.
 * Returns null without a recipient — an empty `mailto:` opens a useless window.
 */
export function buildMailto(
  empfaenger: string | null,
  betreff: string,
  inhalt: string,
): string | null {
  const to = (empfaenger ?? '').trim();
  if (to === '') return null;
  const query = new URLSearchParams({ subject: betreff, body: inhalt }).toString();
  // URLSearchParams encodes spaces as '+', which mail clients show literally.
  return `mailto:${encodeURIComponent(to)}?${query.replace(/\+/g, '%20')}`;
}

/** Percent rendering for an EPSS probability (0…1) in the UI language. */
export function fmtPercent(value: number): string {
  return `${(value * 100).toLocaleString(intlLocale(), { maximumFractionDigits: 1 })} %`;
}
